import { forwardRef } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import cn from './cn.js';

export const inputBase =
  'block w-full rounded-lg border-0 bg-white text-sm text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 transition-shadow duration-150 focus:ring-2 focus:ring-inset focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200';

export const inputInvalid = 'ring-red-300 text-red-900 placeholder:text-red-300 focus:ring-red-500';

const sizes = {
  sm: 'h-8 px-2.5 text-sm',
  md: 'h-9 px-3',
  lg: 'h-10 px-3.5',
};

/** Text input. Supports `invalid`, `size`, `leadingIcon`, `trailing` (node) and `mono`. */
const Input = forwardRef(function Input(
  { className, invalid = false, size = 'md', leadingIcon: LeadingIcon, trailing, mono = false, wrapperClassName, ...props },
  ref,
) {
  const input = (
    <input
      ref={ref}
      className={cn(
        inputBase,
        sizes[size] || sizes.md,
        invalid && inputInvalid,
        LeadingIcon && 'pl-9',
        trailing && 'pr-10',
        mono && 'font-mono text-xs',
        className,
      )}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );

  if (!LeadingIcon && !trailing) return input;

  return (
    <div className={cn('relative', wrapperClassName)}>
      {LeadingIcon && (
        <LeadingIcon
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          aria-hidden="true"
        />
      )}
      {input}
      {trailing && <div className="absolute inset-y-0 right-0 flex items-center pr-2.5">{trailing}</div>}
    </div>
  );
});

export default Input;

/** Search input with a magnifier icon. */
export const SearchInput = forwardRef(function SearchInput({ className, placeholder = 'Search…', ...props }, ref) {
  return (
    <Input
      ref={ref}
      type="search"
      leadingIcon={MagnifyingGlassIcon}
      placeholder={placeholder}
      className={cn('[&::-webkit-search-cancel-button]:appearance-none', className)}
      {...props}
    />
  );
});

/** Multi line input. */
export const Textarea = forwardRef(function Textarea({ className, invalid = false, rows = 3, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(inputBase, 'px-3 py-2 leading-6', invalid && inputInvalid, className)}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );
});

/** Native select styled like Input. */
export const Select = forwardRef(function Select({ className, invalid = false, size = 'md', children, wrapperClassName, ...props }, ref) {
  return (
    <div className={cn('relative', wrapperClassName)}>
      <select
        ref={ref}
        className={cn(
          inputBase,
          sizes[size] || sizes.md,
          'appearance-none pr-9 bg-none',
          invalid && inputInvalid,
          className,
        )}
        aria-invalid={invalid || undefined}
        {...props}
      >
        {children}
      </select>
      <ChevronDownIcon
        className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
        aria-hidden="true"
      />
    </div>
  );
});

/** Checkbox with optional label/description. */
export const Checkbox = forwardRef(function Checkbox({ className, label, description, id, ...props }, ref) {
  const box = (
    <input
      ref={ref}
      id={id}
      type="checkbox"
      className={cn(
        'h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600 transition focus:ring-indigo-500 focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-60',
        className,
      )}
      {...props}
    />
  );

  if (!label) return box;

  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3">
      <span className="flex h-5 items-center">{box}</span>
      <span className="text-sm">
        <span className="font-medium text-gray-900">{label}</span>
        {description && <span className="block text-gray-500">{description}</span>}
      </span>
    </label>
  );
});

/** Radio input. */
export const Radio = forwardRef(function Radio({ className, ...props }, ref) {
  return (
    <input
      ref={ref}
      type="radio"
      className={cn('h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-0', className)}
      {...props}
    />
  );
});
